import { ViewPlugin, Decoration, WidgetType } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

// Widget for rendered horizontal rule
class HRWidget extends WidgetType {
  toDOM() {
    const el = document.createElement('hr');
    el.style.border = 'none';
    el.style.borderTop = '1px solid #e2e8f0';
    el.style.margin = '16px 0';
    return el;
  }
}

// Widget for blockquote marker replacement
class BlockquoteMarkerWidget extends WidgetType {
  toDOM() {
    const el = document.createElement('span');
    el.style.borderLeft = '3px solid #3B82F6';
    el.style.paddingLeft = '4px';
    el.textContent = '';
    return el;
  }
}

// Widget for rendered link
class LinkWidget extends WidgetType {
  constructor(text, url) {
    super();
    this.text = text;
    this.url = url;
  }
  toDOM() {
    const el = document.createElement('a');
    el.textContent = this.text;
    el.href = this.url;
    el.style.color = '#3B82F6';
    el.style.textDecoration = 'underline';
    el.style.textDecorationColor = '#93c5fd';
    el.style.cursor = 'pointer';
    el.onclick = (e) => { e.preventDefault(); };
    return el;
  }
}

// Create decorations that hide markdown syntax when cursor is not on that line
function buildDecorations(view) {
  const decorations = [];
  const { state } = view;
  const cursorLine = state.doc.lineAt(state.selection.main.head).number;

  syntaxTree(state).iterate({
    enter(node) {
      const nodeLine = state.doc.lineAt(node.from).number;
      const isCursorOnNode = cursorLine === nodeLine;

      if (isCursorOnNode) return; // Show raw markdown when cursor is on this line

      // ATX Headings
      if (node.name === 'ATXHeading1' || node.name === 'ATXHeading2' || node.name === 'ATXHeading3' ||
          node.name === 'ATXHeading4' || node.name === 'ATXHeading5' || node.name === 'ATXHeading6') {
        const level = parseInt(node.name.replace('ATXHeading', ''));
        const sizes = { 1: '1.8em', 2: '1.5em', 3: '1.25em', 4: '1.1em', 5: '1.05em', 6: '1em' };
        const weights = { 1: '800', 2: '700', 3: '700', 4: '600', 5: '600', 6: '600' };

        // Find the HeaderMark (the # symbols)
        const line = state.doc.lineAt(node.from);
        const lineText = line.text;
        const match = lineText.match(/^(#{1,6})\s/);
        if (match) {
          const markEnd = line.from + match[0].length;
          // Hide the # marks
          decorations.push(Decoration.replace({}).range(node.from, markEnd));

          // Style the heading text
          if (markEnd < node.to) {
            decorations.push(Decoration.mark({
              attributes: {
                style: `font-size: ${sizes[level]}; font-weight: ${weights[level]}; color: #1d1d1f; line-height: 1.3;`
              }
            }).range(markEnd, node.to));
          }
        }
      }

      // Bold (**text** or __text__)
      if (node.name === 'StrongEmphasis') {
        const text = state.doc.sliceString(node.from, node.to);
        const wrapLen = text.startsWith('**') ? 2 : (text.startsWith('__') ? 2 : 2);
        const openEnd = node.from + wrapLen;
        const closeStart = node.to - wrapLen;

        if (openEnd < closeStart) {
          decorations.push(Decoration.replace({}).range(node.from, openEnd));
          decorations.push(Decoration.replace({}).range(closeStart, node.to));
          decorations.push(Decoration.mark({
            attributes: { style: 'font-weight: 700;' }
          }).range(openEnd, closeStart));
        }
      }

      // Italic (*text* or _text_)
      if (node.name === 'Emphasis') {
        const openEnd = node.from + 1;
        const closeStart = node.to - 1;

        if (openEnd < closeStart) {
          decorations.push(Decoration.replace({}).range(node.from, openEnd));
          decorations.push(Decoration.replace({}).range(closeStart, node.to));
          decorations.push(Decoration.mark({
            attributes: { style: 'font-style: italic;' }
          }).range(openEnd, closeStart));
        }
      }

      // Inline code
      if (node.name === 'InlineCode') {
        const text = state.doc.sliceString(node.from, node.to);
        if (text.startsWith('`') && text.endsWith('`')) {
          decorations.push(Decoration.replace({}).range(node.from, node.from + 1));
          decorations.push(Decoration.replace({}).range(node.to - 1, node.to));
          decorations.push(Decoration.mark({
            attributes: {
              style: 'background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: "SF Mono", "Fira Code", monospace; font-size: 0.9em; color: #e11d48;'
            }
          }).range(node.from + 1, node.to - 1));
        }
      }

      // Blockquote marker (>)
      if (node.name === 'QuoteMark') {
        decorations.push(Decoration.replace({
          widget: new BlockquoteMarkerWidget()
        }).range(node.from, node.to));
      }

      // Blockquote content styling
      if (node.name === 'Blockquote') {
        const line = state.doc.lineAt(node.from);
        decorations.push(Decoration.line({
          attributes: {
            style: 'border-left: 3px solid #3B82F6; padding-left: 12px; color: #6b7280;'
          }
        }).range(line.from));
      }

      // Links [text](url)
      if (node.name === 'Link') {
        let linkText = '';
        let linkUrl = '';
        let hasChildren = false;

        const cursor = node.node.cursor();
        if (cursor.firstChild()) {
          do {
            if (cursor.name === 'LinkLabel') {
              linkText = state.doc.sliceString(cursor.from + 1, cursor.to - 1);
            }
            if (cursor.name === 'URL') {
              linkUrl = state.doc.sliceString(cursor.from, cursor.to);
              hasChildren = true;
            }
          } while (cursor.nextSibling());
        }

        if (hasChildren && linkText) {
          decorations.push(Decoration.replace({
            widget: new LinkWidget(linkText, linkUrl)
          }).range(node.from, node.to));
        }
      }

      // Horizontal rule
      if (node.name === 'HorizontalRule') {
        decorations.push(Decoration.replace({
          widget: new HRWidget()
        }).range(node.from, node.to));
      }
    }
  });

  // Sort decorations by position (required by CodeMirror)
  decorations.sort((a, b) => a.from - b.from || a.startSide - b.startSide);

  return Decoration.set(decorations, true);
}

export const livePreviewPlugin = ViewPlugin.fromClass(class {
  constructor(view) {
    this.decorations = buildDecorations(view);
  }
  update(update) {
    if (update.docChanged || update.selectionSet) {
      this.decorations = buildDecorations(update.view);
    }
  }
}, {
  decorations: v => v.decorations
});
